import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ChevronRight, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { listingSymbol } from '../../utils/regionUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecentlyViewed = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useApp();
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentlyViewed();
  }, [user]);

  const loadRecentlyViewed = async () => {
    setLoading(true);
    try {
      if (isAuthenticated && user?.id) {
        // Load from API for logged in users
        const response = await fetch(`${BACKEND_URL}/api/recently-viewed/${user.id}`);
        if (response.ok) {
          const data = await response.json();
          setRecentProducts(data.products || []);
        }
      } else {
        // Load from localStorage for guests
        const stored = localStorage.getItem('recentlyViewed');
        if (stored) {
          setRecentProducts(JSON.parse(stored));
        }
      }
    } catch (error) {
      console.error('Failed to load recently viewed:', error);
      // Fallback to localStorage
      const stored = localStorage.getItem('recentlyViewed');
      if (stored) {
        setRecentProducts(JSON.parse(stored));
      }
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setRecentProducts([]);
    localStorage.removeItem('recentlyViewed');
  };

  if (loading || recentProducts.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-500" />
          <h2 className="font-semibold text-gray-900">Recently Viewed</h2>
        </div>
        <button
          onClick={clearHistory}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <X className="w-3 h-3" />
          Clear
        </button>
      </div>
      
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
        {recentProducts.slice(0, 10).map(product => (
          <button
            key={product.id}
            onClick={() => navigate(`/product/${product.id}`)}
            className="flex-shrink-0 w-28 text-left"
          >
            <div className="relative">
              <img
                src={product.images?.[0] || 'https://via.placeholder.com/112'}
                alt={product.title}
                className="w-28 h-28 rounded-lg object-cover border border-gray-100"
              />
              {product.sold && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                  <span className="text-white text-xs font-bold">SOLD</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-1 truncate">{product.title}</p>
            <p className="text-sm font-bold text-gray-900">{listingSymbol(product.listing_currency)}{product.price}</p>
          </button>
        ))}
        
        {recentProducts.length > 10 && (
          <button
            onClick={() => navigate('/recently-viewed')}
            className="flex-shrink-0 w-28 h-28 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 hover:border-minted-300 hover:text-minted-600 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
            <span className="text-xs mt-1">View All</span>
          </button>
        )}
      </div>
    </div>
  );
};

// Helper function to track viewed products (call this from ProductDetailPage)
export const trackProductView = async (product, userId) => {
  if (!product) return;
  
  try {
    if (userId) {
      // Track in API for logged in users
      await fetch(`${BACKEND_URL}/api/recently-viewed/add?user_id=${userId}&product_id=${product.id}`, {
        method: 'POST'
      });
    }
    
    // Also store in localStorage as backup
    const stored = localStorage.getItem('recentlyViewed');
    let recent = stored ? JSON.parse(stored) : [];
    
    // Remove if already exists
    recent = recent.filter(p => p.id !== product.id);
    
    // Add to beginning
    recent.unshift({
      id: product.id,
      title: product.title,
      price: product.price,
      images: product.images,
      sold: product.sold
    });
    
    // Keep only 20
    recent = recent.slice(0, 20);
    
    localStorage.setItem('recentlyViewed', JSON.stringify(recent));
  } catch (error) {
    console.error('Failed to track view:', error);
  }
};

export default RecentlyViewed;
